import React from 'react';
import { Award, Zap, Code, Users, TrendingUp, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { ParallaxText } from '../ui/ScrollAnimation';

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const credentials = [
    { 
      icon: <Award className="w-5 h-5" />, 
      text: "Dipl. Softwareentwickler mit über 8 Jahren Erfahrung",
      highlight: "Dipl."
    },
    { 
      icon: <Code className="w-5 h-5" />, 
      text: "Spezialist für moderne Web- und App-Entwicklung",
      highlight: "Spezialist"
    },
    { 
      icon: <TrendingUp className="w-5 h-5" />, 
      text: "Nachgewiesene Erfolge bei über 50 Schweizer KMU",
      highlight: "50+ KMU"
    },
    { 
      icon: <Users className="w-5 h-5" />, 
      text: "Vernetzt in der Schweizer Tech-Community",
      highlight: "Vernetzt"
    },
    { 
      icon: <Zap className="w-5 h-5" />, 
      text: "Garantierte Projektabwicklung nach Schweizer Standards",
      highlight: "Schweizer Standards"
    }
  ];

  const stats = [
    { number: "10+", label: "Jahre Erfahrung", icon: <Award className="w-6 h-6" /> },
    { number: "50+", label: "Schweizer KMU", icon: <Users className="w-6 h-6" /> },
    { number: "CHE", label: "Handelsregister", icon: <TrendingUp className="w-6 h-6" /> },
    { number: "100%", label: "Lokal in Basel", icon: <MapPin className="w-6 h-6" /> }
  ];

  return (
    <section id="about" className="py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30"></div>
      </div>
      
      <motion.div 
        ref={ref}
        className="container mx-auto px-4 relative z-10"
      >
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center justify-center px-6 py-2 mb-6 text-sm font-semibold text-purple-700 bg-purple-100 rounded-full"
            >
              <Award className="w-4 h-4 mr-2" />
              DER RICHTIGE PARTNER
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="block text-gray-900">Warum ich</span>
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
                der Richtige bin.
              </span>
            </h2>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Als Experte für <span className="font-semibold text-gray-900">ultra-qualitative Apps</span> und 
              <span className="font-semibold text-gray-900"> KI-Integration</span> schaffe ich digitale Lösungen, 
              die nicht nur funktionieren – sie <span className="font-semibold text-gray-900">begeistern</span>.
            </p>
            
            <p className="text-lg text-gray-600 mb-10">
              Stell dir vor: Deine täglichen Aufgaben laufen automatisch. Deine Apps arbeiten 
              für dich, nicht umgekehrt. Das ist keine Zukunftsmusik – das ist 
              <span className="font-semibold text-gray-900"> deine neue Realität</span>.
            </p>
            
            <div className="space-y-4 mb-12">
              {credentials.map((credential, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start group"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white mr-4 group-hover:scale-110 transition-transform duration-300">
                    {credential.icon}
                  </div>
                  <p className="text-lg text-gray-700">
                    {credential.text.split(credential.highlight)[0]}
                    <span className="font-semibold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-opacity-10 px-2 py-1 rounded">
                      {credential.highlight}
                    </span>
                    {credential.text.split(credential.highlight)[1]}
                  </p>
                </motion.div>
              ))}
            </div>
            
            <Link to="/termin-buchen">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-black rounded-full overflow-hidden transition-all duration-300 hover:shadow-2xl"
              >
              <span className="relative z-10">Lass uns reden</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600"
                initial={{ x: "-100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
              </motion.button>
            </Link>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2 relative"
          >
            <div className="relative">
              <motion.div 
                animate={{ 
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative z-10"
              >
                <div className="w-80 h-80 md:w-96 md:h-96 rounded-3xl overflow-hidden shadow-2xl mx-auto transform hover:scale-[1.02] transition-transform duration-300">
                  <img 
                    src={`/Foto.png?t=${Date.now()}`} 
                    alt="Patrick Oetterli - Digital Expert" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </motion.div>
              
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full blur-2xl opacity-30"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-8 -left-8 w-40 h-40 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full blur-2xl opacity-30"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-12">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                      {stat.number}
                    </span>
                    <span className="text-gray-400">{stat.icon}</span>
                  </div>
                  <p className="text-gray-600 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
        
        <ParallaxText speed={0.5} className="mt-24 text-center">
          <p className="text-6xl md:text-8xl font-bold text-gray-100">
            SWISS • QUALITY • DIGITAL • EXCELLENCE
          </p>
        </ParallaxText>
      </motion.div>
    </section>
  );
};

export default About;